import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { usePermissions } from '@/hooks/usePermissions';
import { useEntityTypeSchema } from '@/lib/crm/hooks/useEntityTypeSchema';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { EventBuilder } from '../../core/EventBuilder';
import type { EntityTypeName } from '../../core/types';

export interface OutboundChannelState {
  isOutboundChannel: boolean;
  enabled: boolean;
  canToggle: boolean;
  setEnabled: (next: boolean) => void;
}

/**
 * Shared re-enable write shape: re-enabling a channel is always this one
 * write — flip `ahoy_outbound_enabled` back on. Used by both the
 * sub-header toggle (via setEnabled) and the secret-mint re-enable-on-mint
 * convenience.
 */
export function applyOutboundReEnable(
  builder: EventBuilder,
  entityId: string,
  entityTypeName: string
): void {
  builder.propertyUpdated(
    entityId,
    entityTypeName,
    'ahoy_outbound_enabled',
    'on'
  );
}

export function useOutboundChannelState(
  object: CrmObject | null
): OutboundChannelState {
  const { schema } = useEntityTypeSchema(
    object?.org_id,
    object?.entity_type_name as EntityTypeName | undefined
  );
  const { hasPermission } = usePermissions();
  const { user } = useAuthInfo();

  const isOutboundChannel =
    !!object && (schema?.entity_kind.includes('outbound_channel') ?? false);
  // UNSET means ON — only an explicit 'off' disables the channel.
  const enabled =
    getPropertyValue<string>(object?.properties?.ahoy_outbound_enabled) !==
    'off';
  const canToggle = object
    ? hasPermission(object.entity_type_name, 'ahoy_property_updated')
    : false;

  const setEnabled = useCallback(
    (next: boolean) => {
      if (!object || user?.id == null) {
        return;
      }
      void submitEventsOptimistic({
        orgId: object.org_id,
        userId: user.id,
        entityTypeName: object.entity_type_name,
        primaryEntityId: object.entity_id,
        fallbackSeq: object.last_event_seq ?? 0,
        build: builder => {
          if (next) {
            applyOutboundReEnable(
              builder,
              object.entity_id,
              object.entity_type_name
            );
          } else {
            builder.propertyUpdated(
              object.entity_id,
              object.entity_type_name,
              'ahoy_outbound_enabled',
              'off'
            );
          }
        },
        errorToast: {
          message: () => "We couldn't update the channel. Try again.",
        },
      })
        .then(() => {
          toast.success(next ? 'Outbound turned on' : 'Outbound turned off');
        })
        .catch(() => {});
    },
    [object, user?.id]
  );

  return useMemo(
    () => ({
      isOutboundChannel,
      enabled,
      canToggle,
      setEnabled,
    }),
    [isOutboundChannel, enabled, canToggle, setEnabled]
  );
}
